import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { signUpUserWithSDK, signInUserWithSDK, confirmSignUpWithSDK, resendSignUpWithSDK, signInWithGoogleSDK, forgotPasswordWithSDK, confirmForgotPasswordWithSDK } from '../utils/auth-aws-sdk';
import LanguageSwitcher from './LanguageSwitcher';
import { useAppTheme } from '../theme/ThemeProvider';
import { logger } from '../utils/logger';

const { Title, Text } = Typography;

interface AuthModalProps {
  visible: boolean;
  onSuccess: () => void;
}

type AuthMode = 'signin' | 'signup' | 'confirm' | 'forgotPassword' | 'resetPassword';

const PENDING_EMAIL_KEY = 'auth_pending_email';
const AUTH_MODE_KEY = 'auth_mode';
const FORGOT_PASSWORD_EMAIL_KEY = 'auth_forgot_password_email';

const AuthModal: React.FC<AuthModalProps> = ({
  visible,
  onSuccess
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0); // 注册验证码倒计时
  const [forgotPasswordCountdown, setForgotPasswordCountdown] = useState(0); // 忘记密码验证码倒计时

  // 从sessionStorage恢复验证码流程状态
  useEffect(() => {
    if (visible) {
      const savedPendingEmail = sessionStorage.getItem(PENDING_EMAIL_KEY);
      const savedForgotPasswordEmail = sessionStorage.getItem(FORGOT_PASSWORD_EMAIL_KEY);
      const savedMode = sessionStorage.getItem(AUTH_MODE_KEY) as AuthMode | null;
      
      if (savedPendingEmail && savedMode === 'confirm') {
        setPendingEmail(savedPendingEmail);
        setMode('confirm');
        logger.log('恢复验证码流程状态:', { email: savedPendingEmail });
      } else if (savedForgotPasswordEmail && savedMode === 'resetPassword') {
        setPendingEmail(savedForgotPasswordEmail);
        setEmail(savedForgotPasswordEmail);
        setMode('resetPassword');
        logger.log('恢复忘记密码流程状态:', { email: savedForgotPasswordEmail });
      }
    }
  }, [visible]);

  // 注册验证码倒计时
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendCountdown]);

  // 忘记密码验证码倒计时
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (forgotPasswordCountdown > 0) {
      timer = setTimeout(() => {
        setForgotPasswordCountdown(forgotPasswordCountdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [forgotPasswordCountdown]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setVerificationCode('');
    setPendingEmail('');
    setNewPassword('');
    setConfirmNewPassword('');
    // 清除倒计时
    setResendCountdown(0);
    setForgotPasswordCountdown(0);
    // 清除sessionStorage中的状态
    sessionStorage.removeItem(PENDING_EMAIL_KEY);
    sessionStorage.removeItem(AUTH_MODE_KEY);
    sessionStorage.removeItem(FORGOT_PASSWORD_EMAIL_KEY);
  };

  const handleSignIn = async () => {
    if (!email.trim() || !password.trim()) {
      message.warning('请输入邮箱和密码');
      return;
    }

    setLoading(true);
    const result = await signInUserWithSDK(email.trim(), password);
    
    if (result.success) {
      message.success(t('auth.loginSuccess'));
      resetForm(); // 清除sessionStorage中的状态
      onSuccess();
    } else {
      // 检查是否是密码错误
      if (result.error === 'INCORRECT_PASSWORD') {
        message.error(t('auth.incorrectPassword'));
      } else {
        message.error(result.error || t('auth.loginFailed'));
      }
    }
    setLoading(false);
  };

  const handleSignUp = async () => {
    if (!email.trim() || !password.trim()) {
      message.warning('请输入邮箱和密码');
      return;
    }

    if (password !== confirmPassword) {
      message.warning('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    const result = await signUpUserWithSDK(email.trim(), password);
    
    if (result.success) {
      const emailValue = email.trim();
      setPendingEmail(emailValue);
      setMode('confirm');
      // 保存到sessionStorage以便恢复
      sessionStorage.setItem(PENDING_EMAIL_KEY, emailValue);
      sessionStorage.setItem(AUTH_MODE_KEY, 'confirm');
      // 启动60秒倒计时
      setResendCountdown(60);
      message.success('注册成功，请查收验证邮件');
    } else {
      message.error(result.error || '注册失败');
    }
    setLoading(false);
  };

  const handleConfirmSignUp = async () => {
    if (!verificationCode.trim()) {
      message.warning('请输入验证码');
      return;
    }

    setLoading(true);
    const result = await confirmSignUpWithSDK(pendingEmail, verificationCode.trim());
    
    if (result.success) {
      message.success('验证成功');
      resetForm(); // resetForm会清除sessionStorage
      setMode('signin');
    } else {
      message.error(result.error || '验证失败');
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    
    logger.log('=== 开始Google登录流程 ===');
    
    // 使用自定义OAuth处理，直接构建OAuth URL
    logger.log('使用自定义OAuth进行Google登录...');
    const oauthResult = await signInWithGoogleSDK();
    
    if (oauthResult.success) {
      logger.log('自定义OAuth登录成功，准备重定向...');
      message.info('正在重定向到Google登录页面，请稍候...');
      // 不重置loading状态，因为页面即将重定向
    } else {
      logger.log('自定义OAuth登录失败:', oauthResult.error);
      message.error(oauthResult.error || 'Google登录失败');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) {
      return; // 倒计时中，不允许重新发送
    }

    setLoading(true);
    const result = await resendSignUpWithSDK(pendingEmail);
    
    if (result.success) {
      // 启动60秒倒计时
      setResendCountdown(60);
      message.success(t('auth.resendSuccess'));
    } else {
      message.error(result.error || t('auth.resendFailed'));
    }
    setLoading(false);
  };

  const handleResendForgotPasswordCode = async () => {
    if (forgotPasswordCountdown > 0) {
      return; // 倒计时中，不允许重新发送
    }

    setLoading(true);
    const result = await forgotPasswordWithSDK(pendingEmail);
    
    if (result.success) {
      // 启动60秒倒计时
      setForgotPasswordCountdown(60);
      message.success(t('auth.resetCodeSent'));
    } else {
      message.error(result.error || t('auth.resetPasswordFailed'));
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      message.warning(t('auth.enterEmail'));
      return;
    }

    setLoading(true);
    const result = await forgotPasswordWithSDK(email.trim());
    
    if (result.success) {
      const emailValue = email.trim();
      setPendingEmail(emailValue);
      setMode('resetPassword');
      // 保存到sessionStorage以便恢复
      sessionStorage.setItem(FORGOT_PASSWORD_EMAIL_KEY, emailValue);
      sessionStorage.setItem(AUTH_MODE_KEY, 'resetPassword');
      // 启动60秒倒计时
      setForgotPasswordCountdown(60);
      message.success(t('auth.resetCodeSent'));
    } else {
      message.error(result.error || t('auth.resetPasswordFailed'));
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!verificationCode.trim()) {
      message.warning(t('auth.enterVerificationCode'));
      return;
    }

    if (!newPassword.trim()) {
      message.warning(t('auth.enterPassword'));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      message.warning(t('auth.passwordMismatch'));
      return;
    }

    setLoading(true);
    const result = await confirmForgotPasswordWithSDK(pendingEmail, verificationCode.trim(), newPassword);
    
    if (result.success) {
      message.success(t('auth.resetPasswordSuccess'));
      resetForm();
      setMode('signin');
    } else {
      message.error(result.error || t('auth.resetPasswordFailed'));
    }
    setLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (mode === 'signin') {
        handleSignIn();
      } else if (mode === 'signup') {
        handleSignUp();
      } else if (mode === 'confirm') {
        handleConfirmSignUp();
      } else if (mode === 'forgotPassword') {
        handleForgotPassword();
      } else if (mode === 'resetPassword') {
        handleResetPassword();
      }
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={() => {}}
      footer={null}
      closable={false}
      centered
      width={400}
      styles={{
        body: {
          padding: '40px',
          textAlign: 'center',
          background: theme.surfaceBackground,
          position: 'relative'
        }
      }}
    >
      <div style={{ 
        position: 'absolute', 
        top: '16px', 
        right: '16px',
        zIndex: 10
      }}>
        <LanguageSwitcher />
      </div>
      
      <Title level={2} style={{ 
        marginBottom: '32px',
        color: theme.textPrimary,
        fontSize: '24px',
        fontWeight: '500'
      }}>
        {mode === 'signin' ? t('auth.signIn') 
          : mode === 'signup' ? t('auth.signUp') 
          : mode === 'confirm' ? t('auth.verify')
          : mode === 'forgotPassword' ? t('auth.forgotPassword')
          : t('auth.resetPasswordTitle')}
      </Title>

      {mode === 'signin' && (
        <div>
          <Input
            size="large"
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '16px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
            autoFocus
          />
          
          <Input.Password
            size="large"
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '8px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          />
          
          <div style={{ 
            textAlign: 'right', 
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            <Button 
              type="link" 
              onClick={() => {
                setPendingEmail(email.trim() || '');
                setMode('forgotPassword');
              }}
              style={{ 
                padding: '0', 
                height: 'auto', 
                color: theme.accentPrimary,
                fontSize: '14px'
              }}
            >
              {t('auth.forgotPassword')}
            </Button>
          </div>
          
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleSignIn}
            style={{ 
              width: '100%',
              height: '44px',
              marginBottom: '16px',
              background: theme.accentPrimary,
              borderColor: theme.accentPrimary,
              color: theme.surfaceBackground
            }}
          >
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </Button>

          <Button
            size="large"
            loading={loading}
            onClick={handleGoogleSignIn}
            style={{ 
              width: '100%',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          >
            {t('auth.signInWithGoogle')}
          </Button>
        </div>
      )}

      {mode === 'signup' && (
        <div>
          <Input
            size="large"
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '16px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
            autoFocus
          />
          
          <Input.Password
            size="large"
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '16px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          />

          <Input.Password
            size="large"
            placeholder={t('auth.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '24px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          />
          
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleSignUp}
            style={{ 
              width: '100%',
              height: '44px',
              background: theme.accentPrimary,
              borderColor: theme.accentPrimary,
              color: theme.surfaceBackground
            }}
          >
            {loading ? t('auth.signingUp') : t('auth.signUp')}
          </Button>
        </div>
      )}

      {mode === 'confirm' && (
        <div>
          <Text style={{ 
            color: theme.textSecondary,
            display: 'block',
            marginBottom: '24px'
          }}>
            {i18n.language === 'zh-CN' 
              ? `我们已向 ${pendingEmail} 发送了验证码`
              : `We have sent a verification code to ${pendingEmail}`
            }
          </Text>
          
          <Input
            size="large"
            placeholder={t('auth.verificationCode')}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '24px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
            autoFocus
          />
          
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleConfirmSignUp}
            style={{ 
              width: '100%',
              height: '44px',
              marginBottom: '16px',
              background: theme.accentPrimary,
              borderColor: theme.accentPrimary,
              color: theme.surfaceBackground
            }}
          >
            {loading ? t('auth.verifying') : t('auth.verify')}
          </Button>

          <Button
            size="large"
            onClick={handleResendCode}
            loading={loading}
            disabled={resendCountdown > 0}
            style={{ 
              width: '100%',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          >
            {resendCountdown > 0 
              ? (i18n.language === 'zh-CN' ? `重新发送 (${resendCountdown}秒)` : `Resend (${resendCountdown}s)`)
              : t('auth.resendCode')
            }
          </Button>
        </div>
      )}

      {mode === 'forgotPassword' && (
        <div>
          <Text style={{ 
            color: theme.textSecondary,
            display: 'block',
            marginBottom: '24px'
          }}>
            {i18n.language === 'zh-CN' 
              ? '请输入您的邮箱地址，我们将发送验证码到您的邮箱'
              : 'Please enter your email address, we will send a verification code to your email'
            }
          </Text>
          
          <Input
            size="large"
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '24px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
            autoFocus
          />
          
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleForgotPassword}
            style={{ 
              width: '100%',
              height: '44px',
              marginBottom: '16px',
              background: theme.accentPrimary,
              borderColor: theme.accentPrimary,
              color: theme.surfaceBackground
            }}
          >
            {loading ? t('auth.sendingResetCode') : t('auth.sendResetCode')}
          </Button>

          <Button
            size="large"
            onClick={() => {
              setEmail('');
              setMode('signin');
            }}
            style={{ 
              width: '100%',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          >
            {t('auth.backToLogin')}
          </Button>
        </div>
      )}

      {mode === 'resetPassword' && (
        <div>
          <Text style={{ 
            color: theme.textSecondary,
            display: 'block',
            marginBottom: '24px'
          }}>
            {i18n.language === 'zh-CN' 
              ? `我们已向 ${pendingEmail} 发送了验证码，请查收并输入验证码和新密码`
              : `We have sent a verification code to ${pendingEmail}, please check your email and enter the code and new password`
            }
          </Text>
          
          <Input
            size="large"
            placeholder={t('auth.verificationCode')}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '16px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
            autoFocus
          />

          <Input.Password
            size="large"
            placeholder={t('auth.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '16px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          />

          <Input.Password
            size="large"
            placeholder={t('auth.confirmNewPassword')}
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ 
              marginBottom: '24px',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          />
          
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleResetPassword}
            style={{ 
              width: '100%',
              height: '44px',
              marginBottom: '16px',
              background: theme.accentPrimary,
              borderColor: theme.accentPrimary,
              color: theme.surfaceBackground
            }}
          >
            {loading ? t('auth.verifying') : t('auth.resetPassword')}
          </Button>

          <Button
            size="large"
            onClick={handleResendForgotPasswordCode}
            loading={loading}
            disabled={forgotPasswordCountdown > 0}
            style={{ 
              width: '100%',
              height: '44px',
              marginBottom: '16px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          >
            {forgotPasswordCountdown > 0 
              ? (i18n.language === 'zh-CN' ? `重新发送验证码 (${forgotPasswordCountdown}秒)` : `Resend Code (${forgotPasswordCountdown}s)`)
              : t('auth.resendVerificationCode')
            }
          </Button>

          <Button
            size="large"
            onClick={() => {
              resetForm();
              setMode('signin');
            }}
            style={{ 
              width: '100%',
              height: '44px',
              background: theme.surfaceBackground,
              borderColor: theme.surfaceBorder,
              color: theme.textPrimary
            }}
          >
            {t('auth.backToLogin')}
          </Button>
        </div>
      )}
      
      <div style={{ marginTop: '24px' }}>
        {mode === 'signin' ? (
          <span style={{ color: theme.textSecondary }}>
            {t('auth.noAccount')}{' '}
            <Button 
              type="link" 
              onClick={() => setMode('signup')}
              style={{ padding: '0', height: 'auto', color: theme.accentPrimary }}
            >
              {t('auth.signUp')}
            </Button>
          </span>
        ) : mode === 'signup' ? (
          <span style={{ color: theme.textSecondary }}>
            {t('auth.hasAccount')}{' '}
            <Button 
              type="link" 
              onClick={() => setMode('signin')}
              style={{ padding: '0', height: 'auto', color: theme.accentPrimary }}
            >
              {t('auth.signIn')}
            </Button>
          </span>
        ) : mode === 'confirm' ? (
          <span style={{ color: theme.textSecondary }}>
            {i18n.language === 'zh-CN' ? '验证码错误？' : 'Wrong verification code?'}{' '}
            <Button 
              type="link" 
              onClick={() => {
                resetForm(); // 清除sessionStorage中的状态
                setMode('signin');
              }}
              style={{ padding: '0', height: 'auto', color: theme.accentPrimary }}
            >
              {t('auth.backToLogin')}
            </Button>
          </span>
        ) : null}
      </div>
    </Modal>
  );
};

export default AuthModal;